import { createFileRoute, getRouteApi, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { AdminUsersPage } from '@/features/admin-users'
// Through `queries`, not the feature index: a loader is eager by construction.
import { USERS_PAGE_SIZE, adminUsersQuery } from '@/features/admin-users/queries'
import { PERMISSION_ADMIN } from '@/features/auth'

import { can } from '@/app/router/session'

/** Paging and the status slice belong in the URL, so a filtered list is linkable. */
const searchSchema = z.object({
  page: z.number().int().min(0).default(0),
  status: z.enum(['', 'active', 'blocked', 'deleted']).default(''),
})

export const Route = createFileRoute('/_auth/admin/users/')({
  beforeLoad: ({ context }) => {
    if (!can(context, PERMISSION_ADMIN)) throw redirect({ to: '/' })
  },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ page: search.page, status: search.status }),
  // `query: ''` is not a placeholder — it is the value the page mounts with.
  // The search box is local state debounced into the key, so the first render
  // asks for exactly this and finds it already in the cache. Typing after that
  // is an ordinary refetch.
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(
      adminUsersQuery({
        query: '',
        status: deps.status,
        limit: USERS_PAGE_SIZE,
        offset: deps.page * USERS_PAGE_SIZE,
      }),
    ),
  component: AdminUsersRoute,
})

const routeApi = getRouteApi('/_auth/admin/users/')

function AdminUsersRoute() {
  const { page, status } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  return (
    <AdminUsersPage
      page={page}
      onPageChange={(next) => void navigate({ search: (prev) => ({ ...prev, page: next }) })}
      status={status}
      onStatusChange={(next) => void navigate({ search: (prev) => ({ ...prev, status: next }) })}
    />
  )
}
